import React, { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import { ThemeProvider } from '@/components/providers/ThemeProvider'
import { AnimationProvider } from '@/components/providers/AnimationProvider'
import BottomNav from '@/components/BottomNav'
import { GetAppBanner } from '@/components/common/GetAppBanner'
import Analytics from '@/lib/analytics'

// ============================================
// APP LAYOUT
// ============================================

interface AppLayoutProps {
  children: React.ReactNode
  showTabBar?: boolean
}

export function AppLayout({ children, showTabBar = true }: AppLayoutProps) {
  const { pathname } = useLocation()

  // Sayfa geçişlerinde otomatik screen_view event'i
  useEffect(() => {
    Analytics.trackScreen(pathname)
  }, [pathname])

  // PWA kurulum takibi
  useEffect(() => {
    const handler = () => Analytics.appInstalled()
    window.addEventListener('appinstalled', handler)
    return () => window.removeEventListener('appinstalled', handler)
  }, [])

  return (
    <ThemeProvider defaultMode="light" defaultCurrency="GRAM_GOLD">
      <AnimationProvider>
        <div className="min-h-screen bg-background text-foreground">
          {/* Premium Golden Accent Bar — tüm sayfalarda standart */}
          <div className="fixed top-0 left-0 w-full h-[6px] bg-gradient-to-r from-[#D4AF37] via-[#F1D279] to-[#CFA02E] z-50" />
          {/* Web'den giren kullanıcıyı kendi telefonunun mağazasına yönlendirir; uygulamada görünmez */}
          <GetAppBanner />
          {/* Main content with Page Transitions */}
          <main
            className={cn(
              'relative z-0 w-full',
              showTabBar && 'pb-32' // Extra padding for floating dock
            )}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={pathname}
                initial={{ opacity: 0, y: 10, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.98 }}
                transition={{
                  duration: 0.25,
                  ease: [0.32, 0.72, 0, 1],
                }}
                className="relative z-10"
              >
                {children}
              </motion.div>
            </AnimatePresence>
          </main>

          {/* Bottom Nav */}
          {showTabBar && <BottomNav />}
        </div>
      </AnimationProvider>
    </ThemeProvider>
  )
}

// ============================================
// PAGE HEADER
// ============================================

interface PageHeaderProps {
  title: string
  subtitle?: string
  showBack?: boolean
  action?: React.ReactNode
}

export function PageHeader({ title, subtitle, showBack = false, action }: PageHeaderProps) {
  return (
    <motion.header
      className={cn(
        'sticky top-0 z-40 mb-6',
        'bg-[#FCFAF8]/80 backdrop-blur-md', // Glass header
        'border-b border-border/40',
        'pt-[max(env(safe-area-inset-top),_0px)]'
      )}
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
    >
      <div className="flex items-center justify-between h-16 px-6">
        {/* Left */}
        <div className="flex items-center gap-3 min-w-0">
          {showBack && (
            <motion.button
              onClick={() => window.history.back()}
              className="p-2 -ml-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </motion.button>
          )}
          <div className="min-w-0">
            <h1 className="text-xl font-serif font-bold tracking-tight text-foreground truncate">
              {title}
            </h1>
            {subtitle && (
              <p className="text-xs font-medium text-muted-foreground truncate">{subtitle}</p>
            )}
          </div>
        </div>

        {/* Right */}
        {action && <div>{action}</div>}
      </div>
    </motion.header>
  )
}

// ============================================
// SECTION CONTAINER
// ============================================

interface SectionProps {
  children: React.ReactNode
  className?: string
  padding?: boolean
}

export function Section({ children, className, padding = true }: SectionProps) {
  return (
    <motion.section
      className={cn(padding && 'px-6 py-6', className)}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {children}
    </motion.section>
  )
}

export default AppLayout
